import { TenantCredentialId } from "@sp-crm/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ContentFull, ContentHeader } from "components/layout";
import { trpc } from "components/rpc/trpc";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { InlineBanner } from "components/ui/inline-banner";
import { defaultLinkStyle } from "components/ui/link";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import React from "react";
import { Link } from "react-router-dom";
import { useFeature } from "store/selectors/hooks";
import { WellSkyImportSection } from "./wellsky-import-section";
import { WellSkyWebCredentialForm } from "./wellsky-web-credential-form";

export const ManageIntegrationsWellSky: React.FC = () => {
    const isFeatureEnabled = useFeature("wellSkyIntegration");
    const trpcClient = trpc.useTRPC();

    const credentialQuery = useQuery(
        trpcClient.wellSkyPersonalCare.getCredential.queryOptions(undefined, {
            enabled: isFeatureEnabled,
        }),
    );

    const [formOpen, setFormOpen] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
    const [successMessage, setSuccessMessage] = React.useState<string | null>(null);

    const clearMessages = React.useCallback(() => {
        setErrorMessage(null);
        setSuccessMessage(null);
    }, []);

    const showError = React.useCallback((message: string) => {
        setSuccessMessage(null);
        setErrorMessage(message);
    }, []);

    const credential = credentialQuery.data;
    const refetch = credentialQuery.refetch;

    const testMutation = useMutation({
        ...trpcClient.wellSkyPersonalCare.testConnection.mutationOptions(),
        onSuccess: result => {
            setErrorMessage(null);
            setSuccessMessage(`Connected to ${result.accountName} as ${result.username}`);
        },
        onError: error => showError(error.message || "Could not connect to WellSky"),
    });

    const deleteMutation = useMutation({
        ...trpcClient.wellSkyPersonalCare.deleteCredential.mutationOptions(),
        onSuccess: () => {
            setErrorMessage(null);
            setSuccessMessage("WellSky credentials removed");
            refetch();
        },
        onError: error => showError(error.message || "Failed to remove credentials"),
    });

    const handleSaved = React.useCallback(() => {
        setErrorMessage(null);
        setSuccessMessage("Connected to WellSky and saved the credentials");
        setFormOpen(false);
        refetch();
    }, [refetch]);

    const openForm = React.useCallback(() => {
        clearMessages();
        setFormOpen(true);
    }, [clearMessages]);

    const handleRemove = React.useCallback(
        async (id: TenantCredentialId) => {
            const confirmed = await fancyConfirm(
                "Remove WellSky credentials?",
                "The Send to WellSky Personal Care action will be unavailable until new credentials are added, and clients already sent will stop syncing until this WellSky account is connected again.",
                "Yes, remove",
                "Cancel",
            );
            if (confirmed) {
                deleteMutation.mutate({ id });
            }
        },
        [deleteMutation],
    );

    if (!isFeatureEnabled) {
        return (
            <ContentFull>
                <ContentHeader>WellSky Personal Care</ContentHeader>
                <InlineBanner type="info">
                    The WellSky Personal Care integration is not enabled for this account.
                </InlineBanner>
            </ContentFull>
        );
    }

    return (
        <ContentFull>
            <ContentHeader>WellSky Personal Care</ContentHeader>
            <div className="space-y-4">
                <div>
                    Connect your WellSky Personal Care account to send home care clients
                    to WellSky as new prospects. Later changes to a client you have sent
                    are kept in sync in WellSky through this connection.
                </div>

                <InlineBanner type="info">
                    Field requirements for sending a lead to WellSky can be configured in{" "}
                    <Link
                        to="/settings/agency/home-care-clients"
                        className={defaultLinkStyle}>
                        Home Care Client settings
                    </Link>
                    .
                </InlineBanner>

                {successMessage && (
                    <InlineBanner type="success">{successMessage}</InlineBanner>
                )}
                {errorMessage && <InlineBanner type="error">{errorMessage}</InlineBanner>}

                {credentialQuery.isLoading ? (
                    <div className="flex items-center space-x-2">
                        <Spinner />
                        <span>Loading...</span>
                    </div>
                ) : (
                    <>
                        {credential && !formOpen ? (
                            <ConnectedCard
                                fields={[
                                    {
                                        label: "Account domain",
                                        value: credential.accountName,
                                    },
                                    { label: "Username", value: credential.username },
                                ]}
                                testPending={testMutation.isPending}
                                onTest={() => {
                                    clearMessages();
                                    testMutation.mutate();
                                }}
                                onUpdate={openForm}
                                onRemove={() => handleRemove(credential.id)}
                            />
                        ) : null}

                        {!credential && !formOpen && (
                            <PrimaryButton onClick={openForm}>
                                Connect WellSky
                            </PrimaryButton>
                        )}
                    </>
                )}

                {formOpen ? (
                    <div className="p-4 border border-gray-200 rounded space-y-4">
                        <h3 className="font-semibold text-lg">WellSky credentials</h3>
                        <WellSkyWebCredentialForm
                            initialUsername={credential?.username ?? ""}
                            initialAccountName={credential?.accountName ?? ""}
                            onSaved={handleSaved}
                            onCancel={() => setFormOpen(false)}
                            onError={showError}
                        />
                    </div>
                ) : null}

                {credential && !formOpen ? <WellSkyImportSection /> : null}
            </div>
        </ContentFull>
    );
};

interface ConnectedCardField {
    label: string;
    value: string;
}

interface ConnectedCardProps {
    fields: ConnectedCardField[];
    testPending: boolean;
    onTest: () => void;
    onUpdate: () => void;
    onRemove: () => void;
}

const ConnectedCard: React.FC<ConnectedCardProps> = props => (
    <div className="p-3 border border-gray-100 rounded space-y-2">
        {props.fields.map(field => (
            <div key={field.label}>
                <div className="text-sm text-gray-500">{field.label}</div>
                <div className="font-medium">{field.value}</div>
            </div>
        ))}
        <div className="flex space-x-2 pt-2">
            <SecondaryButton onClick={props.onTest} disabled={props.testPending}>
                {props.testPending && <Spinner />}
                <span className={props.testPending ? "ml-2" : ""}>Test connection</span>
            </SecondaryButton>
            <SecondaryButton onClick={props.onUpdate}>Update credentials</SecondaryButton>
            <SecondaryButton onClick={props.onRemove}>Remove</SecondaryButton>
        </div>
    </div>
);
